/**
 * Kundenkonto P1 (Vorgangsdetail, docs/KONZEPT-KUNDENKONTO.md) - Detailansicht EINES eigenen
 * Vorgangs, sitzungsbasiert (Bearer-Session-Token aus localStorage, kein Einzeltoken wie
 * pages/offer-public.tsx). Route `/kundenportal/vorgang/:id`.
 *
 * Ohne (mehr) gueltige Session oder ohne Treffer (fremder/geloeschter Vorgang) landet der Kunde
 * generisch auf der Dashboard-Seite (`/kundenportal`) zurueck - dieselbe Kein-Existenz-Leak-
 * Haltung wie beim Backend (GET .../events/:id liefert fuer beide Faelle 404, s. routes/
 * customer-portal.ts), nur dass hier zusaetzlich `KundenportalPage` selbst entscheidet, ob sie
 * das Login-Formular oder das Dashboard zeigt (Session ggf. laengst abgelaufen).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Icon, ButtonLink } from "../ui/components";
import { BetriebsKopf } from "../ui/BetriebsKopf";
import { PortalBewertungSchalter } from "../ui/PortalBewertungSchalter";
import { anlassAnzeige, eventStatusMeta, money, formatDate, formatDateTime, PILL_CLASSES } from "../ui/format";
import { customerPortalFetch, customerPortalApiError } from "../lib/customer-portal-client";

interface OfferItem {
  id: string;
  dayNo: number | null;
  section: string;
  courseLabel: string | null;
  name: string;
  description: string | null;
  qtyMilli: number;
  unit: string | null;
  unitPriceCents: number;
  priceMode: string;
}

interface EventDetail {
  id: string;
  number: string | null;
  status: string;
  eventDate: string | null;
  dateEnd: string | null;
  timeFrom: string | null;
  timeTo: string | null;
  guestCount: number | null;
  occasion: string | null;
  location: string | null;
  amountTotalCents: number | null;
  createdAt: string;
  updatedAt: string;
  chosenOption: { id: string; label: string; title: string | null; guestCount: number; amountTotalCents: number; items: OfferItem[] } | null;
}

type Phase = "loading" | "ok" | "unavailable";

/** P3: dieselben Felder wie der Betreiber-Weg GET /api/events/:id/cost-acceptance. */
interface CostAcceptance {
  id: string;
  status: string;
  isTest: boolean;
  signUrl: string | null;
  signedPdfUrl: string | null;
  signerName: string | null;
  amountGrossCents: number | null;
  signedAt: string | null;
  statusReason: string | null;
}

/** KÜ V2 (S2, E11): eine zugestellte Version — ersetzte bleiben als Nachweis sichtbar. */
interface KueVersion {
  id: string;
  versionNo: number | null;
  status: "sent" | "signed";
  amountGrossCents: number | null;
  sentAt: string | null;
  signedAt: string | null;
  supersededAt: string | null;
  gilt: boolean;
  pdf: boolean;
  signUrl: string | null;
}

/** Menge lesbar formatieren - ganze Zahlen ohne Nachkommastellen (qtyMilli = Menge * 1000). */
function formatQty(qtyMilli: number): string {
  const qty = qtyMilli / 1000;
  return Number.isInteger(qty) ? String(qty) : qty.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

export default function KundenportalVorgangPage() {
  const { id } = useParams<{ id: string }>();
  const [phase, setPhase] = useState<Phase>("loading");
  const [event, setEvent] = useState<EventDetail | null>(null);
  const [costAcceptance, setCostAcceptance] = useState<CostAcceptance | null>(null);
  const [kueVersionen, setKueVersionen] = useState<KueVersion[]>([]);

  useEffect(() => {
    if (!id) {
      setPhase("unavailable");
      return;
    }
    let cancelled = false;
    setPhase("loading");
    (async () => {
      try {
        const res = await customerPortalFetch(`/api/public/customer-portal/events/${id}`);
        if (!res.ok) throw await customerPortalApiError(res);
        const body = (await res.json()) as { data: EventDetail };
        if (cancelled) return;
        setEvent(body.data);
        setPhase("ok");
      } catch {
        // Sowohl eine abgelaufene Session (CustomerSessionExpiredError, raeumt localStorage
        // bereits selbst auf) als auch ein fremder/nicht existenter Vorgang (404) landen hier -
        // beide zeigen denselben "nicht verfuegbar"-Zustand, kein Existenz-Leak.
        if (cancelled) return;
        setPhase("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    // P3 (Vertragsdokument): eigenstaendiger Nachlade-Schritt, NACH dem Vorgang selbst - ein
    // Fehlschlag hier (inkl. einer zwischenzeitlich abgelaufenen Session) versteckt lediglich den
    // Abschnitt, statt die bereits geladene Vorgangsseite abzureissen (derselbe Best-effort-Geist
    // wie der LexOffice-Live-Abgleich in P2, nur clientseitig statt serverseitig).
    if (!id || phase !== "ok") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await customerPortalFetch(`/api/public/customer-portal/events/${id}/cost-acceptance`);
        if (!res.ok) return;
        const body = (await res.json()) as { data: CostAcceptance | null; versionen?: KueVersion[] };
        if (!cancelled) {
          setCostAcceptance(body.data);
          setKueVersionen(body.versionen ?? []);
        }
      } catch {
        /* Abschnitt bleibt einfach unsichtbar. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, phase]);

  if (phase === "loading") {
    return (
      <div className="mx-auto min-h-screen max-w-3xl px-5 py-12 text-center">
        <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">Wird geladen …</p>
      </div>
    );
  }

  if (phase === "unavailable" || !event) {
    return (
      <div className="mx-auto min-h-screen max-w-3xl px-5 py-12 text-center">
        <Icon name="error" className="mx-auto mb-3 text-[40px] text-error" />
        <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">Nicht verfügbar</h1>
        <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          Dieser Vorgang ist nicht (mehr) erreichbar, oder Ihre Sitzung ist abgelaufen.
        </p>
        <ButtonLink href="/kundenportal" className="mt-6 inline-block !py-3 !px-6">
          Zurück zur Übersicht
        </ButtonLink>
      </div>
    );
  }

  const meta = eventStatusMeta(event.status);

  return (
    <div className="min-h-screen bg-background">
    <BetriebsKopf />
    <div className="mx-auto max-w-3xl px-5 py-12">
      <a href="/kundenportal" className="inline-flex items-center gap-1 font-label-md text-label-md text-secondary hover:underline dark:text-secondary-fixed-dim">
        <Icon name="arrow_back" className="text-[18px]" />
        Zur Übersicht
      </a>
      <div className="mt-4 flex items-center gap-2">
        <h1 className="font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">{anlassAnzeige(event.occasion) || "Vorgang"}</h1>
        <span className={`rounded-md px-1.5 py-0.5 font-label-sm text-label-sm ${PILL_CLASSES[meta.tone]}`}>{meta.label}</span>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DetailZeile label="Termin" value={event.eventDate ? `${formatDate(event.eventDate)}${event.dateEnd ? ` – ${formatDate(event.dateEnd)}` : ""}` : "—"} />
        <DetailZeile label="Uhrzeit" value={event.timeFrom ? `${event.timeFrom}${event.timeTo ? ` – ${event.timeTo}` : ""} Uhr` : "—"} />
        <DetailZeile label="Gäste" value={event.guestCount != null ? String(event.guestCount) : "—"} />
        <DetailZeile label="Ort" value={event.location || "—"} />
      </div>

      {event.chosenOption && (
        <div className="mt-8">
          <h2 className="font-title-md text-title-md text-on-surface dark:text-inverse-on-surface">
            {event.chosenOption.title || "Gewählte Variante"}
          </h2>
          {event.chosenOption.items.length > 0 && (
            <div className="mt-3 overflow-x-auto rounded-xl border border-outline-variant dark:border-white/10">
              <table className="w-full text-left">
                <tbody>
                  {event.chosenOption.items.map((item) => (
                    <tr key={item.id} className="border-b border-outline-variant last:border-0 dark:border-white/10">
                      <td className="px-4 py-3 align-top">
                        <div className="font-body-md text-body-md text-on-surface dark:text-inverse-on-surface">{item.name}</div>
                        {item.description && (
                          <div className="mt-0.5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{item.description}</div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 align-top font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                        {formatQty(item.qtyMilli)} {item.unit || ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-3 text-right font-title-md text-title-md text-on-surface dark:text-inverse-on-surface">
            {money(event.chosenOption.amountTotalCents)}
          </p>
        </div>
      )}

      {costAcceptance && <VertragsdokumentSection ca={costAcceptance} />}
      {kueVersionen.length > 1 && <VertragsVersionen eventId={event.id} versionen={kueVersionen} />}
      <PortalBewertungSchalter eventId={event.id} />
    </div>
    </div>
  );
}

/**
 * P3 (Vertragsdokumente): Kostenuebernahme-Signaturvertrag. Nur gerendert, wenn ueberhaupt einer
 * existiert (kein leerer Abschnitt fuer Vorgaenge ohne Vertrag). 'draft' kommt hier nie an (die
 * Route liefert nur Nicht-Entwurf-Zeilen, s. sql/348).
 */
function VertragsdokumentSection({ ca }: { ca: { status: string; signUrl: string | null; signedPdfUrl: string | null; signedAt: string | null } }) {
  return (
    <div className="mt-8">
      <h2 className="font-title-md text-title-md text-on-surface dark:text-inverse-on-surface">Vertragsdokument</h2>
      <div className="mt-3 flex items-center justify-between gap-4 rounded-xl border border-outline-variant bg-surface px-5 py-4 dark:border-white/10 dark:bg-white/[0.03]">
        {ca.status === "signed" ? (
          <>
            <div>
              <div className="font-body-md text-body-md text-on-surface dark:text-inverse-on-surface">Kostenübernahme unterschrieben</div>
              {ca.signedAt && (
                <div className="mt-0.5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">am {formatDateTime(ca.signedAt)}</div>
              )}
            </div>
            {ca.signedPdfUrl && (
              <ButtonLink href={ca.signedPdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 !py-2 !px-4">
                <Icon name="picture_as_pdf" className="text-[18px]" />
                Vertrag herunterladen
              </ButtonLink>
            )}
          </>
        ) : ca.status === "sent" ? (
          <>
            <div className="font-body-md text-body-md text-on-surface dark:text-inverse-on-surface">Kostenübernahme wartet auf Ihre Unterschrift</div>
            {ca.signUrl && (
              <ButtonLink href={ca.signUrl} target="_blank" rel="noreferrer" className="!py-2 !px-4">
                Jetzt unterschreiben
              </ButtonLink>
            )}
          </>
        ) : (
          <div className="font-body-md text-body-md text-secondary dark:text-secondary-fixed-dim">
            {ca.status === "declined" ? "Kostenübernahme abgelehnt." : "Kostenübernahme zurückgezogen."}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * KÜ V2 (S2, E11 — Antoine 28.09.2026): alle Fassungen der Kostenübernahme als PDF, ersetzte mit
 * „ersetzt am …“. Der Gast hat damit seinen eigenen Nachweis, auch für die frühere Fassung.
 * Welche gilt, sagt der Server (`gilt`) — hier wird nichts entschieden.
 */
function VertragsVersionen({ eventId, versionen }: { eventId: string; versionen: KueVersion[] }) {
  const [fehler, setFehler] = useState<string | null>(null);
  async function pdfLaden(v: KueVersion) {
    setFehler(null);
    try {
      const res = await customerPortalFetch(`/api/public/customer-portal/events/${eventId}/cost-acceptances/${v.id}/pdf`);
      if (!res.ok) throw await customerPortalApiError(res);
      if ((res.headers.get("Content-Type") ?? "").includes("application/json")) {
        const body = (await res.json()) as { data?: { signedPdfUrl?: string } };
        if (body.data?.signedPdfUrl) window.open(body.data.signedPdfUrl, "_blank", "noopener");
        return;
      }
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = `Kostenuebernahme-V${v.versionNo ?? ""}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Das Dokument konnte nicht geladen werden.");
    }
  }
  return (
    <div className="mt-6" data-testid="portal-kue-versionen">
      <h3 className="font-title-sm text-title-sm text-on-surface dark:text-inverse-on-surface">Alle Fassungen</h3>
      <ul className="mt-2 divide-y divide-outline-variant/40 rounded-xl border border-outline-variant bg-surface dark:divide-white/10 dark:border-white/10 dark:bg-white/[0.03]">
        {versionen.map((v) => {
          const stand =
            v.status === "sent"
              ? "wartet auf Ihre Unterschrift"
              : v.gilt
                ? `unterschrieben am ${formatDate(v.signedAt)} · gilt`
                : v.supersededAt
                  ? `unterschrieben am ${formatDate(v.signedAt)} · ersetzt am ${formatDate(v.supersededAt)}`
                  : `unterschrieben am ${formatDate(v.signedAt)}`;
          return (
            <li key={v.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3" data-testid={`portal-kue-version-${v.versionNo ?? "x"}`}>
              <div className="min-w-0">
                <div className="font-body-md text-body-md text-on-surface dark:text-inverse-on-surface">
                  Fassung {v.versionNo ?? "–"}
                  {v.amountGrossCents != null && <span className="text-secondary dark:text-secondary-fixed-dim"> · {money(v.amountGrossCents)}</span>}
                </div>
                <div className="mt-0.5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{stand}</div>
              </div>
              {v.status === "signed" && v.pdf ? (
                <button type="button" onClick={() => void pdfLaden(v)} className="inline-flex items-center gap-1.5 font-label-md text-label-md text-on-surface underline underline-offset-2 dark:text-inverse-on-surface">
                  <Icon name="picture_as_pdf" className="text-[18px]" /> PDF
                </button>
              ) : v.status === "sent" && v.signUrl ? (
                <a href={v.signUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-label-md text-label-md text-on-surface underline underline-offset-2 dark:text-inverse-on-surface">
                  Jetzt unterschreiben <Icon name="open_in_new" className="text-[16px]" />
                </a>
              ) : null}
            </li>
          );
        })}
      </ul>
      {fehler && <p className="mt-2 font-body-sm text-body-sm text-error">{fehler}</p>}
    </div>
  );
}

function DetailZeile({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-label-sm text-label-sm text-secondary dark:text-secondary-fixed-dim">{label}</div>
      <div className="mt-0.5 font-body-md text-body-md text-on-surface dark:text-inverse-on-surface">{value}</div>
    </div>
  );
}
